import React from "react";
import { useQuery } from "@apollo/client";
import { createColumnHelper } from "@tanstack/react-table";
import {
    QUERY_MEETING_METRIC_OPTIONS,
    QUERY_MEETING_METRICS_SUMMARY,
    QUERY_MEETING_INSTANCE_APPLICANTS,
} from "../../../utils/graphql/jobs/queries";
import { GenericReportTable } from "../../../components/reporting/GenericReportTable/GenericReportTable";

function formatDateTime(value) {
    if (!value) return "Unknown";
    const date = new Date(value);
    return date.toLocaleString([], {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatDateOnly(value) {
    if (!value) return "Unknown";
    const date = new Date(value);
    return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
    });
}

function shortenMeetingKey(key) {
    if (!key) return "unknown";
    const value = String(key);
    return value.length > 8 ? value.slice(-8) : value;
}

export function MeetingMetricsPage() {
    const ALL_MEETINGS_KEY = "__ALL__";
    const [selectedMeetingKey, setSelectedMeetingKey] = React.useState(ALL_MEETINGS_KEY);
    const [selectedInstanceKey, setSelectedInstanceKey] = React.useState("");
    const isAllMeetingsSelected = selectedMeetingKey === ALL_MEETINGS_KEY;

    const {
        data: optionsData,
        loading: optionsLoading,
        error: optionsError,
    } = useQuery(QUERY_MEETING_METRIC_OPTIONS, {
        variables: {},
    });

    const meetingOptions = React.useMemo(() => optionsData?.meetingMetricOptions || [], [optionsData]);

    const {
        data: summaryData,
        loading: summaryLoading,
        error: summaryError,
    } = useQuery(QUERY_MEETING_METRICS_SUMMARY, {
        variables: {
            meetingKey: selectedMeetingKey,
        },
        skip: !selectedMeetingKey,
    });

    const summary = summaryData?.meetingMetricsSummary;

    React.useEffect(() => {
        if (!summary?.instances?.length) {
            if (selectedInstanceKey) {
                setSelectedInstanceKey("");
            }
            return;
        }

        if (selectedInstanceKey && !summary.instances.some((instance) => instance.instanceKey === selectedInstanceKey)) {
            setSelectedInstanceKey("");
        }
    }, [summary, selectedInstanceKey]);

    const {
        data: applicantsData,
        loading: applicantsLoading,
        error: applicantsError,
    } = useQuery(QUERY_MEETING_INSTANCE_APPLICANTS, {
        variables: {
            meetingKey: selectedMeetingKey,
            instanceKey: selectedInstanceKey,
        },
        skip: !selectedMeetingKey || !selectedInstanceKey,
    });

    const applicants = React.useMemo(
        () => applicantsData?.meetingInstanceApplicants || [],
        [applicantsData]
    );

    const columnHelper = React.useMemo(() => createColumnHelper(), []);

    const rankingColumns = React.useMemo(
        () => [
            columnHelper.accessor("userName", {
                header: "Peer Parent",
                filterFn: "includesString",
            }),
            columnHelper.accessor("appliedCount", {
                header: "Applied Count",
                meta: { type: "number" },
            }),
            columnHelper.accessor("subCount", {
                header: "Sub Jobs Filled",
                meta: { type: "number" },
            }),
        ],
        [columnHelper]
    );

    const instanceColumns = React.useMemo(
        () => [
            columnHelper.accessor("startDateTime", {
                header: "Instance Date",
                filterFn: "includesString",
                cell: (info) => formatDateOnly(info.getValue()),
            }),
            columnHelper.accessor("submittedByNames", {
                header: "Submitted By",
                cell: (info) => {
                    const value = info.getValue();
                    return Array.isArray(value) && value.length > 0 ? value.join(", ") : "Unknown";
                },
                filterFn: "includesString",
            }),
            columnHelper.accessor("assignedToNames", {
                header: "Who was assigned",
                cell: (info) => {
                    const value = info.getValue();
                    return Array.isArray(value) && value.length > 0 ? value.join(", ") : "Unassigned";
                },
                filterFn: "includesString",
            }),
            columnHelper.accessor("totalApplicants", {
                header: "Total Applicants",
                meta: { type: "number" },
            }),
        ],
        [columnHelper]
    );

    const applicantColumns = React.useMemo(
        () => [
            columnHelper.accessor("userName", {
                header: "Applicant",
                filterFn: "includesString",
            }),
            columnHelper.accessor("appliedAt", {
                header: "Applied At",
                cell: (info) => formatDateTime(info.getValue()),
            }),
            columnHelper.accessor("matchScore", {
                header: "Composite Match Score",
                cell: (info) => {
                    const value = info.getValue();
                    return typeof value === "number" ? value.toFixed(3) : "N/A";
                },
            }),
            columnHelper.accessor("assigned", {
                header: "Assigned",
                cell: (info) => (info.getValue() ? "Yes" : "No"),
                filterFn: "includesString",
            }),
        ],
        [columnHelper]
    );

    if (optionsLoading) {
        return (
            <div className="d-flex justify-content-center p-4">
                <div className="spinner-border" role="status"></div>
            </div>
        );
    }

    if (optionsError || summaryError || applicantsError) {
        return (
            <div className="alert alert-danger">
                Error loading meeting metrics: {(optionsError || summaryError || applicantsError)?.message}
            </div>
        );
    }

    return (
        <div className="metrics-page">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2><i className="bi bi-calendar-week me-2"></i>Meeting Metrics</h2>
                    <small className="text-muted">Per-meeting and per-instance drilldown</small>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-md-12">
                    <select
                        className="form-select form-select-sm"
                        style={{ minWidth: "320px" }}
                        value={selectedMeetingKey}
                        onChange={(e) => {
                            setSelectedMeetingKey(e.target.value);
                            setSelectedInstanceKey("");
                        }}
                    >
                        <option value={ALL_MEETINGS_KEY}>All meetings</option>
                        {meetingOptions.length === 0 && <option value="" disabled>No meetings found</option>}
                        {meetingOptions.map((option) => (
                            <option key={option.meetingKey} value={option.meetingKey}>
                                {option.meetingTitle} ({option.totalSubRequests} requests) [key:{shortenMeetingKey(option.meetingKey)}]
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {summaryLoading ? (
                <div className="d-flex justify-content-center p-4">
                    <div className="spinner-border" role="status"></div>
                </div>
            ) : summary ? (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="card metrics-card">
                                <div className="card-body text-center">
                                    <h3 className="card-title">{summary.totalSubRequests}</h3>
                                    <p className="card-text text-muted">Total Sub Requests</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card metrics-card">
                                <div className="card-body text-center">
                                    <h3 className="card-title">{Number(summary.averageApplicants || 0).toFixed(2)}</h3>
                                    <p className="card-text text-muted">Average Applicants</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card metrics-card">
                                <div className="card-body text-center">
                                    <h3 className="card-title">{summary.uniqueSubs}</h3>
                                    <p className="card-text text-muted">Unique Subs</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card metrics-card">
                                <div className="card-body text-center">
                                    <h3 className="card-title">{summary.neverAssignedApplicants}</h3>
                                    <p className="card-text text-muted">Applied, Never Assigned</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div>
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Ranked Peer Parents</h5>
                                </div>
                                <div className="card-body p-0">
                                    <GenericReportTable
                                        title=""
                                        data={summary.peerParentRanking}
                                        columns={rankingColumns}
                                        initialSorting={[{ id: "subCount", desc: true }]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {!isAllMeetingsSelected && (
                        <div className="row">
                            <div>
                                <div className="card mb-4">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Meeting Instances</h5>
                                        <small className="text-muted">Select an instance row to view applicants</small>
                                    </div>
                                    <div className="card-body p-0">
                                        <GenericReportTable
                                            title=""
                                            data={summary.instances}
                                            columns={instanceColumns}
                                            initialSorting={[{ id: "startDateTime", desc: true }]}
                                            initialFilters={[]}
                                            onRowClick={(row) => setSelectedInstanceKey(row.instanceKey)}
                                            getRowClassName={(row) => (
                                                row.instanceKey === selectedInstanceKey ? "table-active" : ""
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isAllMeetingsSelected && (
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">Applicants for Selected Instance</h5>
                                <small className="text-muted">
                                    {selectedInstanceKey ? `Instance key: ${selectedInstanceKey}` : "Choose an instance above"}
                                </small>
                            </div>
                            <div className="card-body p-0">
                                {applicantsLoading ? (
                                    <div className="d-flex justify-content-center p-4">
                                        <div className="spinner-border" role="status"></div>
                                    </div>
                                ) : (
                                    <GenericReportTable
                                        title=""
                                        data={applicants}
                                        columns={applicantColumns}
                                        // filterFns={filterFns}
                                        initialSorting={[{ id: "appliedAt", desc: false }]}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="alert alert-info">
                    {selectedMeetingKey
                        ? "No meeting metrics available for the selected range."
                        : "Select a meeting to see details."}
                </div>
            )}
        </div>
    );
}
